import { useCallback, useReducer } from 'react'
import { useIntl } from 'react-intl'
import debounce from 'lodash.debounce'
import { useDispatch } from 'react-redux'
import { mergeAll, reject, equals, isEmpty } from 'ramda'
import { exportCsv } from 'app/utilize/generate-csv'
import { exportBeautifulPDF } from 'app/utilize/generate-pdf'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import effects from 'app/effects/effects'
import { renderRoleNames } from 'commons/roleName'
import { networkErrorDetails, networkErrorToMessage } from 'commons/error'
import { getFullPhoneNumber } from 'commons/phoneNumber'
import { generateErrorKeys } from 'helpers/error.helper'

import MessageAlert from 'app/components/MessageAlert'

import {
  ACTIVE,
  INVITED,
  SUSPENDED,
  headerPDF,
  headerCVS,
  INACTIVE,
} from 'app/main/settings/teamMembersV4/constants'

export const ActionType = {
  SET_LOADER_TEAM_MEMBERS: 'SET_LOADER_TEAM_MEMBERS',
  SET_EMPTY_TEAM_MEMBERS: 'SET_EMPTY_TEAM_MEMBERS',
  SET_TEAM_MEMBERS: 'SET_TEAM_MEMBERS',
  SET_IS_SUBMITTING: 'SET_IS_SUBMITTING',
}

export const handleText = (formatMessage, type) => {
  switch (type) {
    case INACTIVE:
      return formatMessage({ id: 'status.inactive' })
    case ACTIVE:
      return formatMessage({ id: 'status.active' })
    case INVITED:
      return formatMessage({ id: 'status.invited' })
    case SUSPENDED:
      return formatMessage({ id: 'status.suspended' })
    default:
      return formatMessage({ id: 'status.active' })
  }
}

const initialState = {
  params: {
    page: 1,
    limit: 10,
  },
  isSubmitting: false,
  isEmptyTeamMembers: false,
  isLoadingTeamMembers: true,
  teamMembers: { data: [], meta: {} },
}

const reducer = (state, action) => {
  switch (action.type) {
    case ActionType.SET_LOADER_TEAM_MEMBERS:
      return {
        ...state,
        isLoadingTeamMembers: action.value,
      }
    case ActionType.SET_EMPTY_TEAM_MEMBERS:
      return {
        ...state,
        isEmptyTeamMembers: true,
        isLoadingTeamMembers: false,
      }
    case ActionType.SET_TEAM_MEMBERS:
      return {
        ...state,
        teamMembers: action.value,
        params: action.params,
        isLoadingTeamMembers: false,
        isSubmitting: false,
        isEmptyTeamMembers: false,
      }
    case ActionType.SET_IS_SUBMITTING:
      return {
        ...state,
        isSubmitting: action.value,
      }
    default:
      return state
  }
}

export default function useTeamMember(
  onCloseDialogTeamMember,
  onResetSelected,
) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { formatMessage } = useIntl()
  const commonDispatch = useDispatch()

  const handleAlertError = useCallback(
    (error) => {
      let errorMessage
      const errorDetails = networkErrorDetails(error)
      if (errorDetails.code === 602) {
        const errors = errorDetails.message.errors
        const errorKeys = generateErrorKeys(errors, formatMessage)

        if (errorKeys.length > 0) {
          errorMessage = (
            <div>
              {errorKeys.map((errorMsg, index) => (
                <p key={index}>{errorMsg}</p>
              ))}
            </div>
          )
        }
      }

      if (!errorMessage) {
        const { message } = networkErrorToMessage(formatMessage)(error)
        errorMessage = message ?? formatMessage({ id: 'error.general' })
      }

      return commonDispatch(
        MessageAlert.showError({
          message: errorMessage,
        }),
      )
    },
    [commonDispatch, formatMessage],
  )

  const fetchTeamMembers = useCallback(
    (params = initialState.params) => {
      effects.apis
        .teamMembers(params)
        .then((response) => {
          if (equals(params, initialState.params) && response.meta.total === 0)
            dispatch({ type: 'SET_EMPTY_TEAM_MEMBERS' })
          else
            dispatch({
              type: 'SET_TEAM_MEMBERS',
              value: response,
              params: params,
            })
        })
        .catch((error) => handleAlertError(error))
    },
    [handleAlertError],
  )

  const handlePayload = (data) => {
    const parsedNumber = isEmpty(data.phoneNumber)
      ? ''
      : parsePhoneNumberFromString(data.phoneNumber)
    return {
      ...data,
      email: data.email.trim(),
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      phoneNumber: parsedNumber?.nationalNumber,
      countryCode: parsedNumber?.countryCallingCode
        ? `+${parsedNumber?.countryCallingCode}`
        : '',
      languageId: data.languageId.id,
    }
  }

  const handleFetchTeamMembers = useCallback(
    async (params) => {
      dispatch({ type: 'SET_LOADER_TEAM_MEMBERS', value: true })
      await fetchTeamMembers(params)
    },
    [fetchTeamMembers],
  )

  const createTeamMembers = useCallback(
    (data) => {
      dispatch({ type: 'SET_IS_SUBMITTING', value: true })
      effects.apis
        .createTeamMember(handlePayload(data))
        .then(async () => {
          onCloseDialogTeamMember()
          await handleFetchTeamMembers(initialState.params)
        })
        .catch((error) => {
          handleAlertError(error)
          dispatch({ type: 'SET_IS_SUBMITTING', value: false })
        })
    },
    [handleFetchTeamMembers, handleAlertError, onCloseDialogTeamMember],
  )

  const updateTeamMembers = useCallback(
    (data) => {
      dispatch({ type: 'SET_IS_SUBMITTING', value: true })
      effects.apis
        .updateTeamMember(handlePayload(data))
        .then(async () => {
          onCloseDialogTeamMember()
          await handleFetchTeamMembers(state.params)
        })
        .catch((error) => {
          handleAlertError(error)
          dispatch({ type: 'SET_IS_SUBMITTING', value: false })
        })
    },
    [
      handleFetchTeamMembers,
      handleAlertError,
      onCloseDialogTeamMember,
      state.params,
    ],
  )

  const debounceFetchTeamMembers = useCallback(
    debounce((value) => {
      dispatch({ type: 'SET_LOADER_TEAM_MEMBERS', value: true })
      fetchTeamMembers(value)
    }, 1000),
    [],
  )

  const handleSearch = (keyword) => {
    const setParams = reject(equals(''))(
      mergeAll([{ ...state.params, keyword: keyword, page: 1, limit: 10 }]),
    )
    debounceFetchTeamMembers(setParams)
  }

  const updateActive = useCallback(
    (id) => {
      effects.apis
        .activeTeamMembers(id)
        .then(() => {
          handleFetchTeamMembers(state.params)
        })
        .catch((error) => {
          handleAlertError(error)
        })
    },
    [handleAlertError, handleFetchTeamMembers, state.params],
  )

  const updateResend = useCallback(
    (id) => {
      effects.apis
        .resendInvitedTeamMembers(id)
        .then(() => {
          handleFetchTeamMembers(state.params)
        })
        .catch((error) => {
          handleAlertError(error)
        })
    },
    [handleAlertError, handleFetchTeamMembers, state.params],
  )

  const updateSuspend = useCallback(
    (id) => {
      effects.apis
        .suspendTeamMembers(id)
        .then(() => {
          handleFetchTeamMembers(state.params)
        })
        .catch((error) => handleAlertError(error))
    },
    [handleAlertError, handleFetchTeamMembers, state.params],
  )

  const deleteTeamMember = useCallback(
    (id) => {
      effects.apis
        .deleteTeamMembers(id)
        .then(async () => {
          await handleFetchTeamMembers(state.params)
          onResetSelected()
        })
        .catch((error) => handleAlertError(error))
    },
    [handleAlertError, handleFetchTeamMembers, onResetSelected, state.params],
  )

  const generateDataExport = (data) => {
    return data.map((value, index) => {
      return {
        row: index + 1,
        firstName: value.firstName,
        lastName: value.lastName,
        roles: renderRoleNames(value),
        email: value.email,
        phone: getFullPhoneNumber(value.countryCode, value.phoneNumber),
        status: handleText(formatMessage, value.status),
      }
    })
  }

  const handleExportPDF = (data, id) => {
    const mappingData = data.filter((item) => id.includes(item.id))
    const headStyles = {
      halign: 'center',
    }
    const columnStyles = {
      0: { cellWidth: 40, halign: 'center' },
      1: { cellWidth: 75 },
      2: { cellWidth: 75 },
      5: { cellWidth: 100 },
      6: { cellWidth: 50 },
    }

    exportBeautifulPDF({
      header: headerPDF(formatMessage),
      body: generateDataExport(mappingData),
      fileName: formatMessage({
        id: 'rolesPermissions.table.teamMembers.label',
      }),
      headStyles,
      columnStyles,
    })
  }

  const handleExportCSV = (data, id) => {
    const mappingData = data.filter((item) => id.includes(item.id))
    exportCsv({
      headerCVS: headerCVS(formatMessage),
      dataCSV: generateDataExport(mappingData),
      fileName: formatMessage({
        id: 'rolesPermissions.table.teamMembers.label',
      }),
    })
  }

  return {
    state,
    handleSearch,
    updateResend,
    updateActive,
    updateSuspend,
    handleExportPDF,
    handleExportCSV,
    deleteTeamMember,
    updateTeamMembers,
    createTeamMembers,
    handleFetchTeamMembers,
  }
}
